import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  Divider,
  Stack
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Category as CategoryIcon,
  LocationOn as LocationIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { disciplinesApi } from '../../services/api';
import NetworkSectionSelector from '../UserManagement/NetworkSectionSelector';

const steps = ['Select Disciplines', 'Select Locations', 'Review & Submit'];

const SUB_DISCIPLINES = ['Pavement Defect', 'Traffic Safety'];

const AccessRequestDialog = () => {
  const { user, authService } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Discipline state
  const [availableDisciplines, setAvailableDisciplines] = useState([]);
  const [selectedDisciplines, setSelectedDisciplines] = useState([]);
  const [selectedSubDisciplines, setSelectedSubDisciplines] = useState([]);

  // Location state
  const [assignableResources, setAssignableResources] = useState({ networks: [] });
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [discResponse, resourcesResponse] = await Promise.all([
        disciplinesApi.getDisciplines(),
        authService.getAvailableResources()
      ]);
      setAvailableDisciplines(discResponse.data || []);
      setAssignableResources(resourcesResponse || { networks: [] });
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('Failed to load configuration. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleDisciplineToggle = (discipline) => {
    const isSelected = selectedDisciplines.some(d => d.id === discipline.id);
    if (isSelected) {
      setSelectedDisciplines(prev => prev.filter(d => d.id !== discipline.id));
      // If removing Road Pavement, clear sub-disciplines
      if (discipline.name === 'Road Pavement') {
        setSelectedSubDisciplines([]);
      }
    } else {
      setSelectedDisciplines(prev => [...prev, { id: discipline.id, name: discipline.name }]);
    }
  };

  const handleSubDisciplineToggle = (subDisc) => {
    const isSelected = selectedSubDisciplines.includes(subDisc);
    if (isSelected) {
      setSelectedSubDisciplines(prev => prev.filter(sd => sd !== subDisc));
    } else {
      setSelectedSubDisciplines(prev => [...prev, subDisc]);
    }
  };

  const isRoadPavementSelected = selectedDisciplines.some(d => d.name === 'Road Pavement');

  const canProceedStep0 = selectedDisciplines.length > 0 &&
    (!isRoadPavementSelected || selectedSubDisciplines.length > 0);

  const canProceedStep1 = selectedPermissions.length > 0;

  const handleNext = () => {
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await authService.submitAccessRequest({
        disciplines: selectedDisciplines,
        subDisciplines: selectedSubDisciplines,
        locations: selectedPermissions
      });
      setSuccess(true);
      // Reload to reinitialize auth state with updated setupStatus
      setTimeout(() => window.location.reload(), 500);
    } catch (err) {
      setError(err.error || 'Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return null; // Dialog closes, PendingApprovalBanner takes over
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select the asset categories (disciplines) you need access to. This determines what types of data you can view in IRIS.
            </Typography>

            <Stack spacing={1}>
              {availableDisciplines.map(discipline => (
                <Paper key={discipline.id} variant="outlined" sx={{ p: 1.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedDisciplines.some(d => d.id === discipline.id)}
                        onChange={() => handleDisciplineToggle(discipline)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" fontWeight={500}>{discipline.name}</Typography>
                        {discipline.description && (
                          <Typography variant="caption" color="text.secondary">{discipline.description}</Typography>
                        )}
                      </Box>
                    }
                    sx={{ m: 0, width: '100%' }}
                  />

                  {/* Sub-disciplines for Road Pavement */}
                  {discipline.name === 'Road Pavement' && isRoadPavementSelected && (
                    <Box sx={{ pl: 4, pt: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        Select sub-disciplines:
                      </Typography>
                      {SUB_DISCIPLINES.map(sd => (
                        <FormControlLabel
                          key={sd}
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedSubDisciplines.includes(sd)}
                              onChange={() => handleSubDisciplineToggle(sd)}
                            />
                          }
                          label={<Typography variant="body2">{sd}</Typography>}
                        />
                      ))}
                    </Box>
                  )}
                </Paper>
              ))}
            </Stack>

            {isRoadPavementSelected && selectedSubDisciplines.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Please select at least one sub-discipline for Road Pavement.
              </Alert>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select the highway networks and/or specific sections you need access to.
              Selecting a network gives you access to all its current and future sections.
            </Typography>

            <NetworkSectionSelector
              assignableResources={assignableResources}
              selectedPermissions={selectedPermissions}
              onChange={setSelectedPermissions}
              error={null}
            />

            {selectedPermissions.length === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Please select at least one network or section.
              </Alert>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Please review your selections below. Your disciplines will be applied immediately.
              Location access requires manager approval.
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>Disciplines</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selectedDisciplines.map(d => (
                  <Chip key={d.id} label={d.name} size="small" color="primary" variant="outlined" />
                ))}
              </Box>
              {selectedSubDisciplines.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">Sub-disciplines:</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {selectedSubDisciplines.map(sd => (
                      <Chip key={sd} label={sd} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Requested Locations
                <Chip label="Requires Approval" size="small" color="warning" sx={{ ml: 1 }} />
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selectedPermissions.map(p => (
                  <Chip
                    key={`${p.type}-${p.id}`}
                    label={`${p.type === 'network' ? 'Network' : 'Section'}: ${p.name}`}
                    size="small"
                    color={p.type === 'network' ? 'info' : 'default'}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={true}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      onClose={(event, reason) => {
        // Prevent closing
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
      }}
      PaperProps={{ sx: { minHeight: '60vh' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddIcon color="primary" />
          <Box>
            <Typography variant="h6">Welcome to IRIS</Typography>
            <Typography variant="body2" color="text.secondary">
              Hi {user?.fullName || user?.username}, please set up your access preferences
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          renderStepContent()
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={submitting}>
            Back
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={
              (activeStep === 0 && !canProceedStep0) ||
              (activeStep === 1 && !canProceedStep1)
            }
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {submitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AccessRequestDialog;
